import { DateTime } from 'luxon';

/**
 * Interface representing an invoice from the real data source
 */
export interface Invoice {
  /** Unique invoice number */
  invoice_no: string;

  /** Start date of the billing period (YYYY-MM-DD) */
  period_start_date: string;

  /** End date of the billing period (YYYY-MM-DD) */
  period_end_date: string;

  /** The invoice amount */
  amount: number;

  /** Type of invoice (e.g., 'energy', 'service') */
  invoice_type: string;

  /** Date when the invoice was issued (YYYY-MM-DD) */
  emission_date: string;

  /** Date when the invoice is due (YYYY-MM-DD) */
  due_date: string;

  /** Whether the invoice has been paid */
  is_paid: boolean;

  /** Date when the invoice was paid, or null if not paid (YYYY-MM-DD | null) */
  payment_date: string | null;

  /** ID of the site to which this invoice belongs */
  siteId: string;

  /** ID of the user associated with this invoice */
  userId: string;

  /** URL to the invoice PDF document */
  url: string;
  /** URL to the invoice PDF document */
  base64Image: string;

  /** Period identifier for the invoice (e.g., '2025_Q2') */
  invoice_period: string;

  /** Currency symbol for the invoice (e.g., '€', 'CHF', '£') */
  unit?: string;
}

/**
 * Interface for the transformed structure that matches the 'data' format
 */
export interface TransformedData {
  /** Month identifier in YYYY-MM format */
  month: string;

  /** Start date of the billing period (YYYY-MM-DD) */
  period_start_date?: string;

  /** End date of the billing period (YYYY-MM-DD) */
  period_end_date?: string;

  /** Array of site-specific invoice information */
  sites: Array<{
    /** Name of the site */
    siteName: string;

    /** Point of delivery identifier */
    pod: string;

    /** Invoice identifier */
    invoice: string;

    /** Invoice amount */
    amount: number;

    /** Payment status */
    paid: boolean;

    /** Date when the invoice was issued */
    invoiceDate: string;

    /** Invoice ID for reference */
    invoiceID: string;

    /** Due date for the invoice */
    invoiceDueDate: string;

    /** Currency unit */
    unit: string;

    /** Original invoice data preserved */
    [key: string]: any;
  }>;

  /** Total amount for the period */
  amount?: number;

  /** Total received (paid) amount for the period */
  received?: number;
}
const ENV = (import.meta as any).env;

export function formatMonth(selectedDate: [Date, Date]) {
  return `${(selectedDate?.[0] || selectedDate).toLocaleString(
    ENV.VITE_APP_LOCALE,
    { month: 'long', year: 'numeric' }
  )}`;
}

function parseInvoiceDate(date?: string | null): Date | null {
  if (!date) {
    return null;
  }

  const parsedDate = DateTime.fromISO(String(date), { zone: 'utc' });
  if (parsedDate.isValid) {
    return parsedDate.toJSDate();
  }

  const fallbackDate = new Date(date);
  return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
}

function toIsoDate(date: Date | null): string | undefined {
  return date
    ? DateTime.fromJSDate(date, { zone: 'utc' }).toFormat('yyyy-MM-dd')
    : undefined;
}

function getInvoicePeriodKey(invoice: Invoice): string {
  const startDate = parseInvoiceDate(invoice.period_start_date);
  const endDate = parseInvoiceDate(invoice.period_end_date);
  const startDateKey = toIsoDate(startDate);
  const endDateKey = toIsoDate(endDate);

  if (startDateKey && endDateKey) {
    return `${startDateKey}|${endDateKey}`;
  }

  return startDateKey || endDateKey || invoice.invoice_period || 'unknown';
}

function formatInvoicePeriodDate(date: Date): string {
  return DateTime.fromJSDate(date, { zone: 'utc' }).toFormat('dd/MM/yyyy');
}

function formatPeriodLabel(startDate?: string, endDate?: string, fallback = '') {
  const parsedStartDate = parseInvoiceDate(startDate);
  const parsedEndDate = parseInvoiceDate(endDate);

  if (!parsedStartDate || !parsedEndDate) {
    return fallback || startDate || endDate || '';
  }

  return `${formatInvoicePeriodDate(parsedStartDate)} - ${formatInvoicePeriodDate(
    parsedEndDate
  )}`;
}

function getPeriodBounds(invoices: Invoice[]) {
  const validStartDates = invoices
    .map((invoice) => parseInvoiceDate(invoice.period_start_date))
    .filter(Boolean) as Date[];
  const validEndDates = invoices
    .map((invoice) => parseInvoiceDate(invoice.period_end_date))
    .filter(Boolean) as Date[];

  const periodStartDate =
    validStartDates.length > 0
      ? new Date(Math.min(...validStartDates.map((date) => date.getTime())))
      : null;
  const periodEndDate =
    validEndDates.length > 0
      ? new Date(Math.max(...validEndDates.map((date) => date.getTime())))
      : null;

  return {
    periodStartDate,
    periodEndDate,
  };
}

export function isValidKwickInvoice(invoice: any) {
  const value = `${invoice?.is_valid}`.toLowerCase();
  return (
    invoice?.is_valid !== false &&
    value !== 'f' &&
    value !== 'false' &&
    value !== '0'
  );
}

/**
 * Transforms realDataSample data into the same structure as the data array
 * Groups invoices by their billing period and formats appropriately
 *
 * @param realData - The raw invoice data from realDataSample
 * @returns Data structured in the same format as the 'data' variable
 */
export function transformRealDataToStructuredData(
  realData: Invoice[],
  metadata: any
): TransformedData[] {
  // Guard: If realData is not a valid array, return empty array
  // This prevents crashes when API returns null, undefined, or non-array values
  if (!Array.isArray(realData) || realData.length === 0) {
    return [];
  }

  // Group invoices by their effective billing period
  const groupedByPeriod: Record<string, Invoice[]> = {};

  realData.forEach((invoice) => {
    const period = getInvoicePeriodKey(invoice);
    if (!groupedByPeriod[period]) {
      groupedByPeriod[period] = [];
    }
    groupedByPeriod[period].push(invoice);
  });

  // Transform the grouped data to match the structure of 'data'
  const transformedData = Object.entries(groupedByPeriod).map(
    ([period, invoices]) => {
      const { periodStartDate, periodEndDate } = getPeriodBounds(invoices);
      const startDate = toIsoDate(periodStartDate);
      const endDate = toIsoDate(periodEndDate);
      const month = formatPeriodLabel(startDate, endDate, period);

      // Transform each invoice to match the 'sites' structure
      const sites = invoices.map((invoice: Invoice) => {
        return {
          siteName: invoice.siteId,
          pod: invoice.siteId,
          invoice: invoice.invoice_no,
          paid: invoice.is_paid,
          invoiceDate: invoice.emission_date,
          invoiceID: invoice.invoice_no,
          invoiceDueDate: invoice.due_date,
          ...invoice,
          ...(metadata?.entities?.sites || []).find(
            (site: any) => site.siteId === invoice.siteId
          ),
        };
      });

      // Calculate period-level aggregations
      const validSites = sites.filter(isValidKwickInvoice);
      const amount = validSites.reduce(
        (sum, site) => sum + (site.amount || 0),
        0
      );
      const received = validSites.reduce(
        (sum, site) => sum + (site.paid ? site.amount || 0 : 0),
        0
      );

      return {
        month,
        period_start_date: startDate,
        period_end_date: endDate,
        sites,
        amount,
        received,
        originalPeriod: period,
      };
    }
  );

  return transformedData
    .sort((a, b) => {
      const startA = parseInvoiceDate(a.period_start_date)?.getTime() || 0;
      const startB = parseInvoiceDate(b.period_start_date)?.getTime() || 0;
      const endA = parseInvoiceDate(a.period_end_date)?.getTime() || 0;
      const endB = parseInvoiceDate(b.period_end_date)?.getTime() || 0;

      return startB - startA || endB - endA;
    })
    .map(
      ({
        month,
        period_start_date,
        period_end_date,
        sites,
        amount,
        received,
      }) => ({
        month,
        period_start_date,
        period_end_date,
        sites,
        amount,
        received,
      })
    );
}

/**
 * Sort sites within each month by the specified column
 * @param storedRows - The current stored rows data
 * @param columnId - The column ID to sort by
 * @param rowsOrder - Current sort order [columnId, direction]
 * @param setRowsOrder - Function to update the sort order state
 * @returns The sorted data array
 */
export function sortRows(
  storedRows: TransformedData[],
  columnId: string,
  rowsOrder: [string, string],
  setRowsOrder: (order: [string, string]) => void
): TransformedData[] {
  try {
    // Create a deep copy of the stored rows
    let _midArray = storedRows.map((month) => ({
      ...month,
      sites: [...month.sites],
    }));

    // Determine sort direction (toggle if same column clicked)
    let direction = 'asc';
    if (rowsOrder[0] === columnId && rowsOrder[1] === 'asc') {
      direction = 'desc';
    }

    // Sort sites within each month
    _midArray = _midArray.map((month) => {
      const sortedSites = [...month.sites];

      switch (columnId) {
        case 'site':
          sortedSites.sort((a, b) =>
            (
              direction === 'asc'
                ? a.siteName > b.siteName
                : a.siteName < b.siteName
            )
              ? 1
              : -1
          );
          break;
        case 'resident':
          sortedSites.sort((a, b) =>
            (
              direction === 'asc'
                ? `${a.memberFirstName} ${a.memberLastName}` >
                  `${b.memberFirstName} ${b.memberLastName}`
                : `${a.memberFirstName} ${a.memberLastName}` <
                  `${b.memberFirstName} ${b.memberLastName}`
            )
              ? 1
              : -1
          );
          break;
        case 'pod':
          sortedSites.sort((a, b) =>
            (direction === 'asc' ? a.pod > b.pod : a.pod < b.pod) ? 1 : -1
          );
          break;
        case 'invoice':
          sortedSites.sort((a, b) =>
            (
              direction === 'asc'
                ? a.invoiceID > b.invoiceID
                : a.invoiceID < b.invoiceID
            )
              ? 1
              : -1
          );
          break;
        case 'amount':
          sortedSites.sort((a, b) =>
            (direction === 'asc' ? a.amount > b.amount : a.amount < b.amount)
              ? 1
              : -1
          );
          break;
        case 'invoiceDate':
          sortedSites.sort((a, b) => {
            const d1 = new Date(a.invoiceDate);
            const d2 = new Date(b.invoiceDate);
            return (direction === 'asc' ? d1 > d2 : d1 < d2) ? 1 : -1;
          });
          break;
        case 'invoiceDueDate':
          sortedSites.sort((a, b) => {
            const d1 = new Date(a.invoiceDueDate);
            const d2 = new Date(b.invoiceDueDate);
            return (direction === 'asc' ? d1 > d2 : d1 < d2) ? 1 : -1;
          });
          break;
        case 'paid':
          sortedSites.sort((a, b) =>
            (
              direction === 'asc'
                ? Number(a.paid) > Number(b.paid)
                : Number(a.paid) < Number(b.paid)
            )
              ? 1
              : -1
          );
          break;
      }

      // Return the month object with sorted sites
      return {
        ...month,
        sites: sortedSites,
      };
    });

    // Update state with new sort order and sorted data
    setRowsOrder([columnId, direction]);
    return _midArray;
  } catch (err: any) {
    console.warn(err);
    return storedRows;
  }
}

export const openPdfWithTitle = (b64: string, name: string) => {
  const filename = (name || 'document')
    .trim()
    .replace(/[^\w.-]+/g, '_')
    .toLowerCase()
    .endsWith('.pdf')
    ? name.trim().replace(/[^\w.-]+/g, '_')
    : `${(name || 'document').trim().replace(/[^\w.-]+/g, '_')}.pdf`;

  const byteChars = atob(b64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);

  const file = new File([bytes], filename, { type: 'application/pdf' });
  const url = URL.createObjectURL(file);
  window.open(url, '_blank', 'noopener,noreferrer');
  // opzionale: libera l’URL dopo un po’
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};
